import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  TrendingUp,
  Calendar,
  Download,
  Search,
  Eye,
  AlertCircle,
} from 'lucide-react'
import { evaluationsApi } from '../../lib/api/evaluations'
import { downloadHtmlAsPdf } from '../../lib/utils/htmlToPdf'

// ── Types ──────────────────────────────────────────────────────────────────────

interface BreakdownThemeNotes {
  went_well: string
  needs_work: string
  example: string
}

interface Evaluation {
  id: number
  status: string
  client_name: string | null
  session_title: string | null
  scheduled_at: string | null
  delivered_at: string | null
  overall_score: number | null
  summary: string | null
  observation_notes: string | null
  transcript_excerpts: string[]
  strengths: string[]
  improvements: string[]
  practice_actions: string[]
  red_flags: string[]
  notes: string | null
  communication_score: number | null
  technical_score: number | null
  problem_solving_score: number | null
  leadership_score: number | null
  cultural_fit_score: number | null
  interview_readiness: string | null
  key_insight: string | null
  confidence_delivery_summary: string | null
  performance_breakdown_notes: Record<string, BreakdownThemeNotes> | null
}

interface CompletableSession {
  evaluation_id: number | null
  evaluation_status: string | null
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

function statusLabel(status: string) {
  if (status === 'delivered') return 'Delivered'
  if (status === 'draft') return 'Draft'
  if (status === 'interviewer_review') return 'Under Review'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function cleanTitle(title: string | null) {
  if (!title || title === 'Mock Interview Session') return null
  return title
}

// ── Question Progress types (mirrors GET /api/evaluations/:id/questions) ───

interface QuestionBundleCategory { key: string; label: string; display_order: number }
interface QuestionBundleResponse {
  scores: Record<string, number>
  note: string | null
  recommendation: string | null
  completion_status: string
}
interface QuestionBundleItem {
  question_index: number
  question_text: string
  question_type_name: string | null
  categories: QuestionBundleCategory[]
  response: QuestionBundleResponse | null
}
interface QuestionBundle {
  total: number
  completed: number
  in_progress: number
  items: QuestionBundleItem[]
}

// Map 1-5 score to a word label for the PDF
function scoreLabel(n: number): string {
  return (
    { 1: 'Very Weak', 2: 'Weak', 3: 'Mixed', 4: 'Strong', 5: 'Very Strong' }[n] ?? '—'
  )
}

function scorePillColor(n: number): { bg: string; color: string } {
  if (n >= 4) return { bg: '#dcfce7', color: '#15803d' }   // green
  if (n === 3) return { bg: '#fef3c7', color: '#92400e' }  // amber
  return { bg: '#fee2e2', color: '#b91c1c' }                // red
}

function renderQuestionProgressHtml(bundle: QuestionBundle | null): string {
  if (!bundle || bundle.total === 0) return ''

  const items = bundle.items
    .map((it, idx) => {
      const scores = it.response?.scores ?? {}
      const note = (it.response?.note ?? '').trim()
      const rec = (it.response?.recommendation ?? '').trim()

      const sortedCats = it.categories.slice().sort((a, b) => a.display_order - b.display_order)
      const workedItems = sortedCats.filter((c) => {
        const v = scores[c.key]; return typeof v === 'number' && v >= 4
      })
      const improveItems = sortedCats.filter((c) => {
        const v = scores[c.key]; return typeof v === 'number' && v <= 3
      })

      const recColors: Record<string, { bg: string; color: string }> = {
        keep:    { bg: '#dcfce7', color: '#15803d' },
        improve: { bg: '#fef3c7', color: '#92400e' },
        replace: { bg: '#fee2e2', color: '#b91c1c' },
      }
      const recColor = rec ? (recColors[rec] ?? { bg: '#f3e8ff', color: '#6b21a8' }) : null
      const recLabel: Record<string, string> = { keep: 'Keep Answer', improve: 'Improve Answer', replace: 'Replace Answer' }

      const typeColors: Record<string, { bg: string; color: string }> = {
        'behavioral':             { bg: '#dbeafe', color: '#1d4ed8' },
        'situational':            { bg: '#e0e7ff', color: '#4338ca' },
        'role-specific':          { bg: '#fce7f3', color: '#be185d' },
        'tell me about yourself': { bg: '#ecfdf5', color: '#059669' },
        'motivation / fit':       { bg: '#fef3c7', color: '#92400e' },
        'test question':          { bg: '#f1f5f9', color: '#64748b' },
      }
      const typeKey = (it.question_type_name ?? '').toLowerCase()
      const typeColor = Object.entries(typeColors).find(([k]) => typeKey.includes(k))?.[1] ?? { bg: '#f3e8ff', color: '#7c3aed' }

      return `<div style="margin-top:${idx === 0 ? 0 : 14}px;border:1px solid #e2e8f0;border-radius:8px;padding:12px 14px;page-break-inside:avoid;break-inside:avoid;background:#fafbfc;">

        <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;">
          <span style="display:inline-block;min-width:24px;height:24px;border-radius:50%;background:#1e2432;color:white;font-size:10px;font-weight:700;text-align:center;line-height:24px;flex-shrink:0;">${idx + 1}</span>
          <div style="flex:1;">
            ${it.question_type_name ? `<div style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;background:${typeColor.bg};color:${typeColor.color};margin-bottom:4px;">${it.question_type_name}</div>` : ''}
            <div style="font-size:11px;font-weight:700;color:#1e2432;line-height:1.4;">${it.question_text}</div>
          </div>
          ${recColor ? `<span style="display:inline-block;padding:2px 10px;border-radius:10px;font-size:8px;font-weight:700;white-space:nowrap;background:${recColor.bg};color:${recColor.color};flex-shrink:0;">${recLabel[rec] ?? rec}</span>` : ''}
        </div>

        <table style="width:100%;border-collapse:collapse;margin-top:6px;">
          <tr>
            <td style="width:33%;padding-right:8px;vertical-align:top;">
              <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:#16a34a;margin-bottom:4px;padding-bottom:2px;border-bottom:1px solid #bbf7d0;">What Worked</div>
              ${workedItems.length > 0
                ? workedItems.map((c) => {
                    const v = scores[c.key]
                    const cs = scorePillColor(v)
                    return `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:3px;"><span style="font-size:9px;color:#14532d;">${c.label}</span><span style="font-size:8px;font-weight:600;padding:1px 6px;border-radius:8px;background:${cs.bg};color:${cs.color};">${scoreLabel(v)}</span></div>`
                  }).join('')
                : `<p style="font-size:9px;color:#9ca3af;font-style:italic;margin:0;">—</p>`
              }
            </td>
            <td style="width:33%;vertical-align:top;border-left:1px solid #e2e8f0;padding-left:8px;">
              <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:#d97706;margin-bottom:4px;padding-bottom:2px;border-bottom:1px solid #fde68a;">What Could Improve</div>
              ${improveItems.length > 0
                ? improveItems.map((c) => {
                    const v = scores[c.key]
                    const cs = scorePillColor(v)
                    return `<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:3px;"><span style="font-size:9px;color:#78350f;">${c.label}</span><span style="font-size:8px;font-weight:600;padding:1px 6px;border-radius:8px;background:${cs.bg};color:${cs.color};">${scoreLabel(v)}</span></div>`
                  }).join('')
                : `<p style="font-size:9px;color:#9ca3af;font-style:italic;margin:0;">—</p>`
              }
            </td>
            <td style="width:33%;padding-left:8px;vertical-align:top;border-left:1px solid #e2e8f0;">
              <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:#4338ca;margin-bottom:4px;padding-bottom:2px;border-bottom:1px solid #c7d2fe;">Suggested Approach</div>
              ${note
                ? `<p style="font-size:9px;color:#1e3a8a;margin:0;line-height:1.4;">${note}</p>`
                : `<p style="font-size:9px;color:#9ca3af;font-style:italic;margin:0;">No notes added</p>`
              }
            </td>
          </tr>
        </table>

      </div>`
    })
    .join('')

  return `
    <h2 style="font-size:11px;font-weight:700;color:#4338ca;margin:16px 0 6px;padding-bottom:4px;border-bottom:2px solid #4338ca;">Question-by-Question Analysis</h2>
    <div class="section-box">
      <div style="font-size:9px;color:#4b5563;margin-bottom:10px;">
        ${bundle.completed} of ${bundle.total} questions evaluated${bundle.in_progress > 0 ? ` · ${bundle.in_progress} in progress` : ''}
      </div>
      ${items}
    </div>
  `
}

async function fetchQuestionBundle(evaluationId: number): Promise<QuestionBundle | null> {
  try {
    return await evaluationsApi.getQuestionEvaluationBundle(evaluationId)
  } catch {
    return null
  }
}

async function downloadPdf(ev: Evaluation) {
  const bundle = await fetchQuestionBundle(ev.id)
  await _downloadPdfDirect(ev, bundle)
}

function _buildSingleReportHtml(ev: Evaluation, bundle: QuestionBundle | null): string {
  const list = (items: string[], color = '#1a202c') =>
    (items ?? []).length
      ? `<ul style="margin:2px 0 0;padding-left:16px;">${(items ?? []).map((s) => `<li style="color:${color};margin-bottom:2px;font-size:10px;">${esc(s)}</li>`).join('')}</ul>`
      : '<p style="color:#718096;font-style:italic;margin:2px 0 0;font-size:10px;">None recorded</p>'

  const scoreRow = (label: string, val: number | null) =>
    `<tr>
      <td style="padding:4px 10px;font-size:10px;border-bottom:1px solid #e2e8f0;">${label}</td>
      <td style="padding:4px 10px;font-size:10px;border-bottom:1px solid #e2e8f0;font-weight:600;">
        ${val != null ? `${val}/5` : '—'}
      </td>
      <td style="padding:4px 10px;border-bottom:1px solid #e2e8f0;">
        ${val != null ? `<div style="background:#e2e8f0;border-radius:3px;height:6px;width:80px;overflow:hidden;">
          <div style="background:#2d3748;width:${(val / 5) * 100}%;height:100%;border-radius:3px;"></div>
        </div>` : ''}
      </td>
    </tr>`

  const stripEmoji = (s: string) =>
    s.replace(/[\u{10000}-\u{10FFFF}]|[✂-➰]|[︀-️]|‍/gu, '')
  const esc = (s: string) =>
    stripEmoji(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  const safe = (fn: () => string): string => { try { return fn() } catch { return '' } }
  const nl2br = (s: string) => esc(s).replace(/\n/g, '<br>')

  const summaryHtml = ev.summary ? nl2br(ev.summary) : ''
  const observationHtml = ev.observation_notes ? nl2br(ev.observation_notes) : ''
  const notesHtml = ev.notes ? nl2br(ev.notes) : ''

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Evaluation Report – ${ev.client_name ?? 'Client'}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, sans-serif; padding: 24px; color: #1a202c; margin: 0 auto; font-size: 10px; line-height: 1.5; }
    h1 { font-size: 16px; margin: 0 0 3px; }
    .meta { color: #718096; font-size: 10px; margin-bottom: 18px; }
    .badge { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 8px; font-weight: 700; background: #dcfce7; color: #15803d; margin-left: 6px; vertical-align: middle; }
    h2 { font-size: 11px; font-weight: 700; color: #2d3748; margin: 16px 0 6px; padding-bottom: 4px; border-bottom: 1px solid #e2e8f0; }
    table { width: 100%; border-collapse: collapse; margin-top: 3px; }
    th { background: #f7fafc; padding: 4px 10px; text-align: left; font-size: 8px; text-transform: uppercase; letter-spacing: 0.4px; color: #718096; border-bottom: 1px solid #e2e8f0; }
    td { padding: 4px 10px; font-size: 10px; border-bottom: 1px solid #e2e8f0; }
    .section-box { background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 14px; margin-top: 3px; }
    blockquote { margin: 0 0 6px; padding: 6px 10px; border-left: 2px solid #818cf8; background: #f5f3ff; border-radius: 0 4px 4px 0; font-style: italic; font-size: 10px; }
    .overall { font-size: 18px; font-weight: 700; color: #2d3748; }
    p { font-size: 10px; }
  </style>
</head>
<body>
  <h1>Evaluation Report <span class="badge">${statusLabel(ev.status)}</span></h1>
  <div class="meta">
    ${ev.client_name ?? 'Unknown Client'}
    ${cleanTitle(ev.session_title) ? ` — ${cleanTitle(ev.session_title)}` : ''}
    &nbsp;|&nbsp; Date: ${formatDate(ev.delivered_at ?? ev.scheduled_at)}
    ${ev.overall_score != null ? `&nbsp;|&nbsp; Overall Score: ${ev.overall_score}/5` : ''}
  </div>

  <h2>Session Details</h2>
  <div class="section-box">
    <table>
      <tr><th>Field</th><th>Value</th></tr>
      <tr><td>Client</td><td style="font-weight:600;">${ev.client_name ?? '—'}</td></tr>
      ${cleanTitle(ev.session_title) ? `<tr><td>Targeted Role</td><td>${cleanTitle(ev.session_title)}</td></tr>` : ''}
      <tr><td>Scheduled Date</td><td>${formatDate(ev.scheduled_at)}</td></tr>
      <tr><td>Delivered Date</td><td>${formatDate(ev.delivered_at)}</td></tr>
      <tr><td>Status</td><td>${statusLabel(ev.status)}</td></tr>
      ${ev.overall_score != null ? `<tr><td>Overall Score</td><td><span class="overall">${ev.overall_score}/5</span></td></tr>` : ''}
    </table>
  </div>

  ${renderQuestionProgressHtml(bundle)}

  ${(() => {
    const readinessMap: Record<string, { label: string; bg: string; color: string }> = {
      strong:    { label: 'Strong',     bg: '#dcfce7', color: '#15803d' },
      developing:{ label: 'Developing', bg: '#fef9c3', color: '#a16207' },
      needs_work:{ label: 'Needs Work', bg: '#fee2e2', color: '#b91c1c' },
    }
    const parts: string[] = []
    if (ev.interview_readiness && readinessMap[ev.interview_readiness]) {
      const r = readinessMap[ev.interview_readiness]
      parts.push(`<span style="display:inline-block;padding:3px 12px;border-radius:12px;background:${r.bg};color:${r.color};font-size:10px;font-weight:700;margin-bottom:8px;">${r.label}</span>`)
    }
    if (ev.key_insight) parts.push(`<div style="margin-bottom:6px;padding:6px 10px;background:#eff6ff;border-left:3px solid #3b82f6;border-radius:0 4px 4px 0;"><div style="font-size:9px;font-weight:700;color:#1d4ed8;margin-bottom:2px;">KEY INSIGHT</div><p style="font-size:10px;color:#1e3a8a;margin:0;">${esc(ev.key_insight)}</p></div>`)
    if (ev.confidence_delivery_summary) parts.push(`<div style="padding:6px 10px;background:#fdf4ff;border-left:3px solid #a855f7;border-radius:0 4px 4px 0;"><div style="font-size:9px;font-weight:700;color:#7e22ce;margin-bottom:2px;">CONFIDENCE &amp; DELIVERY</div><p style="font-size:10px;color:#581c87;margin:0;">${esc(ev.confidence_delivery_summary)}</p></div>`)
    return parts.length ? `<h2>Interview Overview</h2><div class="section-box">${parts.join('')}</div>` : ''
  })()}

  <h2>Competency Scores</h2>
  <table>
    <thead><tr><th>Competency</th><th>Score</th><th>Visual</th></tr></thead>
    <tbody>
      ${scoreRow('Communication', ev.communication_score)}
      ${scoreRow('Technical', ev.technical_score)}
      ${scoreRow('Problem Solving', ev.problem_solving_score)}
      ${scoreRow('Leadership', ev.leadership_score)}
      ${scoreRow('Cultural Fit', ev.cultural_fit_score)}
    </tbody>
  </table>

  ${safe(() => {
    const bdNotes = ev.performance_breakdown_notes
    if (!bdNotes) return ''
    const themes = [
      { slug: 'communication',            label: 'Communication' },
      { slug: 'content_quality',          label: 'Content Quality' },
      { slug: 'confidence_presence',      label: 'Confidence &amp; Presence' },
      { slug: 'role_alignment',           label: 'Role Alignment' },
      { slug: 'thinking_problem_solving', label: 'Thinking &amp; Problem Solving' },
    ]
    const rows = themes.map((t) => {
      const n = bdNotes[t.slug]
      if (!n || (!n.went_well && !n.needs_work && !n.example)) return ''
      return `<div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:10px;page-break-inside:avoid;break-inside:avoid;">
        <div style="display:flex;align-items:center;padding:8px 12px;background:#f8fafc;border-bottom:1px solid #e2e8f0;">
          <span style="font-size:11px;font-weight:700;color:#1e2432;">${t.label}</span>
        </div>
        <table style="width:100%;border-collapse:collapse;">
          <tr>
            ${n.went_well ? `<td style="width:33%;padding:8px 10px;vertical-align:top;border-right:1px solid #e2e8f0;"><div style="font-size:8px;font-weight:700;color:#16a34a;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px;">What Went Well</div><p style="font-size:9px;color:#14532d;margin:0;line-height:1.5;">${esc(n.went_well)}</p></td>` : '<td></td>'}
            ${n.needs_work ? `<td style="width:33%;padding:8px 10px;vertical-align:top;border-right:1px solid #e2e8f0;"><div style="font-size:8px;font-weight:700;color:#d97706;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px;">What Needs Work</div><p style="font-size:9px;color:#78350f;margin:0;line-height:1.5;">${esc(n.needs_work)}</p></td>` : '<td></td>'}
            ${n.example ? `<td style="width:33%;padding:8px 10px;vertical-align:top;"><div style="font-size:8px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px;">Example</div><p style="font-size:9px;color:#334155;margin:0;line-height:1.5;font-style:italic;">${esc(n.example)}</p></td>` : '<td></td>'}
          </tr>
        </table>
      </div>`
    }).join('')
    return rows ? `<h2 style="font-size:11px;font-weight:700;color:#7c3aed;margin:16px 0 6px;padding-bottom:4px;border-bottom:2px solid #7c3aed;">Performance Breakdown</h2>${rows}` : ''
  })}

  <h2>Strengths</h2>
  <div class="section-box">${list(ev.strengths ?? [], '#15803d')}</div>

  <h2>Areas to Improve</h2>
  <div class="section-box">${list(ev.improvements ?? [], '#92400e')}</div>

  <h2>Practice Actions &amp; Next Steps</h2>
  <div class="section-box">${list(ev.practice_actions ?? [], '#1d4ed8')}</div>

  ${(ev.red_flags ?? []).length > 0 ? `
  <h2>Red Flags</h2>
  <div class="section-box" style="border-color:#fecaca;background:#fff5f5;">${list(ev.red_flags, '#dc2626')}</div>
  ` : ''}

  ${ev.observation_notes ? `
  <h2>Notes &amp; Observations / Narrative Assessment</h2>
  <div class="section-box" style="page-break-inside:avoid;break-inside:avoid;"><p style="margin:0;word-break:break-word;overflow-wrap:break-word;">${observationHtml}</p></div>
  ` : ''}

  ${(ev.transcript_excerpts ?? []).length > 0 ? `
  <h2>Transcript Excerpts</h2>
  <div class="section-box">
    ${(ev.transcript_excerpts).map((t) => `<blockquote>${t}</blockquote>`).join('')}
  </div>
  ` : ''}

  ${ev.summary ? `
  <h2>AI-Generated Summary</h2>
  <div class="section-box" style="border-color:#e9d5ff;background:#faf5ff;page-break-inside:avoid;break-inside:avoid;">
    <p style="margin:0;word-break:break-word;overflow-wrap:break-word;">${summaryHtml}</p>
  </div>
  ` : ''}

  ${ev.notes ? `
  <h2>Private Notes</h2>
  <div class="section-box" style="page-break-inside:avoid;break-inside:avoid;"><p style="margin:0;word-break:break-word;overflow-wrap:break-word;">${notesHtml}</p></div>
  ` : ''}

</body>
</html>`
}

async function _downloadPdfDirect(ev: Evaluation, bundle: QuestionBundle | null) {
  const html = _buildSingleReportHtml(ev, bundle)
  const clientName = (ev.client_name ?? 'Client').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '-')
  const dateStr = new Date(ev.delivered_at ?? ev.scheduled_at ?? Date.now()).toISOString().slice(0, 10)
  const filename = `Evaluation-Report-${clientName}-${dateStr}.pdf`
  await downloadHtmlAsPdf(html, filename)
}

async function exportAllPdf(evals: Evaluation[]) {
  // Fetch bundles in parallel; any failures return null and are skipped in render.
  const bundles = await Promise.all(evals.map((ev) => fetchQuestionBundle(ev.id)))
  await _downloadAllPdfDirect(evals, bundles)
}

function _buildAllReportsHtml(evals: Evaluation[], bundles: (QuestionBundle | null)[]): string {
  const stripEmoji = (s: string) =>
    s.replace(/[\u{10000}-\u{10FFFF}]|[✂-➰]|[︀-️]|‍/gu, '')
  const esc = (s: string) =>
    stripEmoji(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

  const list = (items: string[], color = '#1a202c') =>
    (items ?? []).length
      ? `<ul style="margin:2px 0 0;padding-left:16px;">${(items ?? []).map((s) => `<li style="color:${color};margin-bottom:2px;font-size:10px;">${esc(s)}</li>`).join('')}</ul>`
      : '<p style="color:#718096;font-style:italic;margin:2px 0 0;font-size:10px;">None recorded</p>'

  const scoreRow = (label: string, val: number | null) =>
    `<tr>
      <td style="padding:4px 10px;font-size:10px;border-bottom:1px solid #e2e8f0;">${label}</td>
      <td style="padding:4px 10px;font-size:10px;border-bottom:1px solid #e2e8f0;font-weight:600;">${val != null ? `${val}/5` : '—'}</td>
      <td style="padding:4px 10px;border-bottom:1px solid #e2e8f0;">${val != null ? `<div style="background:#e2e8f0;border-radius:3px;height:6px;width:80px;overflow:hidden;"><div style="background:#2d3748;width:${(val / 5) * 100}%;height:100%;border-radius:3px;"></div></div>` : ''}</td>
    </tr>`

  const reportBlock = (ev: Evaluation, isLast: boolean, bundle: QuestionBundle | null) => `
    <div style="page-break-after:${isLast ? 'avoid' : 'always'};">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;">
        <h1 style="font-size:15px;margin:0;">Evaluation Report</h1>
        <span style="display:inline-block;padding:1px 8px;border-radius:10px;font-size:8px;font-weight:700;background:#dcfce7;color:#15803d;">${statusLabel(ev.status)}</span>
      </div>
      <div style="color:#718096;font-size:10px;margin-bottom:14px;padding-bottom:10px;border-bottom:1px solid #e2e8f0;">
        ${ev.client_name ?? 'Unknown Client'}
        ${cleanTitle(ev.session_title) ? ` — ${cleanTitle(ev.session_title)}` : ''}
        &nbsp;|&nbsp; Date: ${formatDate(ev.delivered_at ?? ev.scheduled_at)}
        ${ev.overall_score != null ? `&nbsp;|&nbsp; Overall Score: ${ev.overall_score}/5` : ''}
      </div>

      <h2>Session Details</h2>
      <div class="section-box">
        <table><tr><th>Field</th><th>Value</th></tr>
          <tr><td>Client</td><td style="font-weight:600;">${ev.client_name ?? '—'}</td></tr>
          ${cleanTitle(ev.session_title) ? `<tr><td>Targeted Role</td><td>${cleanTitle(ev.session_title)}</td></tr>` : ''}
          <tr><td>Scheduled Date</td><td>${formatDate(ev.scheduled_at)}</td></tr>
          <tr><td>Delivered Date</td><td>${formatDate(ev.delivered_at)}</td></tr>
          <tr><td>Status</td><td>${statusLabel(ev.status)}</td></tr>
          ${ev.overall_score != null ? `<tr><td>Overall Score</td><td style="font-size:14px;font-weight:700;">${ev.overall_score}/5</td></tr>` : ''}
        </table>
      </div>

      ${renderQuestionProgressHtml(bundle)}

      ${(() => {
        const readinessMap: Record<string, { label: string; bg: string; color: string }> = {
          strong:    { label: 'Strong',     bg: '#dcfce7', color: '#15803d' },
          developing:{ label: 'Developing', bg: '#fef9c3', color: '#a16207' },
          needs_work:{ label: 'Needs Work', bg: '#fee2e2', color: '#b91c1c' },
        }
        const parts: string[] = []
        if (ev.interview_readiness && readinessMap[ev.interview_readiness]) {
          const r = readinessMap[ev.interview_readiness]
          parts.push(`<span style="display:inline-block;padding:2px 10px;border-radius:12px;background:${r.bg};color:${r.color};font-size:9px;font-weight:700;margin-bottom:6px;">${r.label}</span>`)
        }
        if (ev.key_insight) parts.push(`<div style="margin-bottom:4px;padding:5px 8px;background:#eff6ff;border-left:2px solid #3b82f6;border-radius:0 3px 3px 0;font-size:9px;color:#1e3a8a;"><strong>Key Insight:</strong> ${ev.key_insight}</div>`)
        if (ev.confidence_delivery_summary) parts.push(`<div style="padding:5px 8px;background:#fdf4ff;border-left:2px solid #a855f7;border-radius:0 3px 3px 0;font-size:9px;color:#581c87;"><strong>Confidence &amp; Delivery:</strong> ${ev.confidence_delivery_summary}</div>`)
        return parts.length ? `<h2>Interview Overview</h2><div class="section-box">${parts.join('')}</div>` : ''
      })()}

      <h2>Competency Scores</h2>
      <table><thead><tr><th>Competency</th><th>Score</th><th>Visual</th></tr></thead><tbody>
        ${scoreRow('Communication', ev.communication_score)}
        ${scoreRow('Technical', ev.technical_score)}
        ${scoreRow('Problem Solving', ev.problem_solving_score)}
        ${scoreRow('Leadership', ev.leadership_score)}
        ${scoreRow('Cultural Fit', ev.cultural_fit_score)}
      </tbody></table>

      ${(() => {
        const bdNotes = ev.performance_breakdown_notes
        if (!bdNotes) return ''
        const themes = [
          { slug: 'communication',            label: 'Communication' },
          { slug: 'content_quality',          label: 'Content Quality' },
          { slug: 'confidence_presence',      label: 'Confidence &amp; Presence' },
          { slug: 'role_alignment',           label: 'Role Alignment' },
          { slug: 'thinking_problem_solving', label: 'Thinking &amp; Problem Solving' },
        ]
        const rows = themes.map((t) => {
          const n = bdNotes[t.slug]
          if (!n || (!n.went_well && !n.needs_work && !n.example)) return ''
          return `<div style="border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin-bottom:10px;page-break-inside:avoid;break-inside:avoid;">
            <div style="display:flex;align-items:center;padding:8px 12px;background:#f8fafc;border-bottom:1px solid #e2e8f0;">
              <span style="font-size:11px;font-weight:700;color:#1e2432;">${t.label}</span>
            </div>
            <table style="width:100%;border-collapse:collapse;">
              <tr>
                ${n.went_well ? `<td style="width:33%;padding:8px 10px;vertical-align:top;border-right:1px solid #e2e8f0;"><div style="font-size:8px;font-weight:700;color:#16a34a;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px;">What Went Well</div><p style="font-size:9px;color:#14532d;margin:0;line-height:1.5;">${n.went_well}</p></td>` : '<td></td>'}
                ${n.needs_work ? `<td style="width:33%;padding:8px 10px;vertical-align:top;border-right:1px solid #e2e8f0;"><div style="font-size:8px;font-weight:700;color:#d97706;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px;">What Needs Work</div><p style="font-size:9px;color:#78350f;margin:0;line-height:1.5;">${n.needs_work}</p></td>` : '<td></td>'}
                ${n.example ? `<td style="width:33%;padding:8px 10px;vertical-align:top;"><div style="font-size:8px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:4px;">Example</div><p style="font-size:9px;color:#334155;margin:0;line-height:1.5;font-style:italic;">${n.example}</p></td>` : '<td></td>'}
              </tr>
            </table>
          </div>`
        }).join('')
        return rows ? `<h2 style="font-size:11px;font-weight:700;color:#7c3aed;margin:16px 0 6px;padding-bottom:4px;border-bottom:2px solid #7c3aed;">Performance Breakdown</h2>${rows}` : ''
      })()}

      <h2>Strengths</h2>
      <div class="section-box">${list(ev.strengths ?? [], '#15803d')}</div>

      <h2>Areas to Improve</h2>
      <div class="section-box">${list(ev.improvements ?? [], '#92400e')}</div>

      <h2>Practice Actions &amp; Next Steps</h2>
      <div class="section-box">${list(ev.practice_actions ?? [], '#1d4ed8')}</div>

      ${(ev.red_flags ?? []).length > 0 ? `<h2>Red Flags</h2><div class="section-box" style="border-color:#fecaca;background:#fff5f5;">${list(ev.red_flags, '#dc2626')}</div>` : ''}
      ${ev.observation_notes ? `<h2>Notes &amp; Observations / Narrative Assessment</h2><div class="section-box"><p style="margin:0;white-space:pre-wrap;font-size:10px;">${ev.observation_notes}</p></div>` : ''}
      ${(ev.transcript_excerpts ?? []).length > 0 ? `<h2>Transcript Excerpts</h2><div class="section-box">${ev.transcript_excerpts.map((t) => `<blockquote>${t}</blockquote>`).join('')}</div>` : ''}
      ${ev.summary ? `<h2>AI-Generated Summary</h2><div class="section-box" style="border-color:#e9d5ff;background:#faf5ff;"><p style="margin:0;white-space:pre-wrap;font-size:10px;">${ev.summary}</p></div>` : ''}
      ${ev.notes ? `<h2>Private Notes</h2><div class="section-box"><p style="margin:0;white-space:pre-wrap;font-size:10px;">${ev.notes}</p></div>` : ''}
    </div>`

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>All Evaluation Reports</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, sans-serif; padding: 24px; color: #1a202c; margin: 0 auto; font-size: 10px; line-height: 1.5; }
    h2 { font-size: 11px; font-weight: 700; color: #2d3748; margin: 14px 0 5px; padding-bottom: 3px; border-bottom: 1px solid #e2e8f0; }
    table { width: 100%; border-collapse: collapse; margin-top: 3px; }
    th { background: #f7fafc; padding: 4px 10px; text-align: left; font-size: 8px; text-transform: uppercase; letter-spacing: 0.4px; color: #718096; border-bottom: 1px solid #e2e8f0; }
    td { padding: 4px 10px; font-size: 10px; border-bottom: 1px solid #e2e8f0; }
    .section-box { background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; margin-top: 3px; }
    blockquote { margin: 0 0 6px; padding: 6px 10px; border-left: 2px solid #818cf8; background: #f5f3ff; border-radius: 0 4px 4px 0; font-style: italic; font-size: 10px; }
    p { font-size: 10px; }
  </style>
</head>
<body>
  <div style="text-align:center;padding:18px 0 22px;border-bottom:2px solid #2d3748;margin-bottom:24px;">
    <h1 style="font-size:18px;font-weight:700;margin:0 0 4px;color:#1a202c;">Evaluation Reports</h1>
    <p style="color:#718096;font-size:10px;margin:0;">Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} &nbsp;·&nbsp; ${evals.length} report${evals.length !== 1 ? 's' : ''}</p>
  </div>
  ${evals.map((ev, i) => reportBlock(ev, i === evals.length - 1, bundles[i] ?? null)).join('\n')}
</body>
</html>`
}

async function _downloadAllPdfDirect(evals: Evaluation[], bundles: (QuestionBundle | null)[]) {
  const html = _buildAllReportsHtml(evals, bundles)
  const dateStr = new Date().toISOString().slice(0, 10)
  const filename = `All-Evaluation-Reports-${dateStr}.pdf`
  await downloadHtmlAsPdf(html, filename)
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function InterviewerReports() {
  const navigate = useNavigate()
  const [evals, setEvals] = useState<Evaluation[]>([])
  const [needsEvalCount, setNeedsEvalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    Promise.all([
      evaluationsApi.getEvaluations({ per_page: 100 }).catch(() => ({ data: [] })),
      evaluationsApi.getCompletableSessions().catch(() => []),
    ]).then(([evalsRes, completable]) => {
      const evData: Evaluation[] = evalsRes.data || []
      setEvals(evData)
      const sessions: CompletableSession[] = Array.isArray(completable) ? completable : []
      // Matches the Evaluations page's "pending" badge exactly: both read
      // evaluation_status straight off the completable-sessions response
      // (server-side join), not a separately-paginated evaluations list —
      // that used to drift out of sync once an interviewer had more
      // evaluations than either page's per_page cap.
      setNeedsEvalCount(
        sessions.filter((s) => {
          const status = s.evaluation_status
          return !status || status === 'draft' || status === 'interviewer_review'
        }).length
      )
    }).finally(() => setLoading(false))
  }, [])

  const now = new Date()
  const thisMonthCount = evals.filter((e) => {
    const d = new Date(e.delivered_at ?? e.scheduled_at ?? '')
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  }).length
  const deliveredCount = evals.filter((e) => e.status === 'delivered').length

  const filtered = evals.filter((e) => {
    const q = search.toLowerCase()
    return (
      (e.client_name ?? '').toLowerCase().includes(q) ||
      (e.session_title ?? '').toLowerCase().includes(q)
    )
  })

  if (loading) return <div className="loading-screen">Loading reports...</div>

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Reports</h1>
        <p className="page-subtitle">View and export completed evaluation reports</p>
      </div>

      {/* ── Stat cards ── */}
      <div className="irep-stats-grid">

        {/* Total Reports */}
        <div className="card" style={{ padding: 24, display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FileText size={20} color="#64748b" />
          </div>
          <div>
            <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--text-dark)', lineHeight: 1 }}>{evals.length}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 5 }}>Total Reports</div>
          </div>
        </div>

        {/* Delivered */}
        <div className="card" style={{ padding: 24, display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <TrendingUp size={20} color="#22c55e" />
          </div>
          <div>
            <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--text-dark)', lineHeight: 1 }}>{deliveredCount}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 5 }}>Delivered</div>
          </div>
        </div>

        {/* This Month */}
        <div className="card" style={{ padding: 24, display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Calendar size={20} color="#f97316" />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.3px', marginBottom: 3 }}>This Month</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--text-dark)', lineHeight: 1 }}>{thisMonthCount} Reports</div>
          </div>
        </div>

        {/* Needs Evaluation */}
        <div className="card" style={{ padding: 24, display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(217,119,6,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AlertCircle size={20} color="#d97706" />
          </div>
          <div>
            <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--text-dark)', lineHeight: 1 }}>{needsEvalCount}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 5 }}>Needs Evaluation</div>
          </div>
        </div>
      </div>

      {/* ── Completed Reports list ── */}
      <div className="card">
        <div className="irep-header-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)' }}>Completed Reports</h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, justifyContent: 'flex-end', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px', background: 'white', flex: 1, maxWidth: 260, minWidth: 0 }}>
              <Search size={15} color="#9ca3af" style={{ flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search reports..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="irep-search-input"
                style={{ border: 'none', outline: 'none', fontSize: 13, color: 'var(--text-dark)', background: 'transparent', width: '100%', minWidth: 0 }}
              />
            </div>
            {evals.length > 0 && (
              <button
                className="btn btn-primary"
                style={{ fontSize: 13, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0 }}
                onClick={() => exportAllPdf(evals)}
              >
                <Download size={15} /> Export All
              </button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: '48px 0' }}>
            <FileText size={44} style={{ opacity: 0.3, marginBottom: 12 }} />
            <h3>{evals.length === 0 ? 'No evaluations yet' : 'No results found'}</h3>
            <p>{evals.length === 0 ? 'Completed evaluations will appear here.' : 'Try a different search term.'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {filtered.map((ev, idx) => {
              const date = formatDate(ev.delivered_at ?? ev.scheduled_at)
              const isDelivered = ev.status === 'delivered'
              const statusColor = isDelivered
                ? { bg: '#dcfce7', color: '#15803d' }
                : ev.status === 'draft'
                ? { bg: '#fef3c7', color: '#92400e' }
                : { bg: '#e0f2fe', color: '#0369a1' }

              return (
                <div
                  key={ev.id}
                  className="irep-report-row"
                  style={{ borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none' }}
                >
                  {/* Icon */}
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FileText size={17} color="#64748b" />
                  </div>

                  {/* Body: info + meta + buttons */}
                  <div className="irep-report-body">
                    {/* Client info */}
                    <div className="irep-report-info">
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ev.client_name ?? 'Unknown Client'}
                      </div>
                      {ev.session_title && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.session_title}</div>
                      )}
                    </div>

                    {/* Date + status badge */}
                    <div className="irep-report-meta">
                      <div style={{ fontSize: 13, color: '#3b82f6', fontWeight: 500, whiteSpace: 'nowrap' }}>{date}</div>
                      <span style={{
                        display: 'inline-block', marginTop: 4,
                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                        background: statusColor.bg, color: statusColor.color, whiteSpace: 'nowrap',
                      }}>
                        {ev.status === 'interviewer_review' ? 'Under Review' : ev.status.charAt(0).toUpperCase() + ev.status.slice(1)}
                      </span>
                    </div>

                    {/* Action buttons */}
                    <div className="irep-report-btns">
                      <button
                        className="btn btn-outline"
                        style={{ fontSize: 13, padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        onClick={() => navigate(`/interviewer/evaluations?evalId=${ev.id}`)}
                      >
                        <Eye size={14} /> View
                      </button>
                      <button
                        className="btn btn-outline"
                        style={{ fontSize: 13, padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        onClick={() => downloadPdf(ev)}
                      >
                        <Download size={14} /> PDF
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <style>{`
        .irep-stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 28px;
        }
        @media (max-width: 1024px) { .irep-stats-grid { grid-template-columns: repeat(2, 1fr); } }
        /* Mobile: always 2×2, never 1-column */
        @media (max-width: 480px)  {
          .irep-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
          .irep-stats-grid .card { padding: 16px 12px !important; gap: 10px !important; }
          .irep-stats-grid .card > div:first-child { width: 36px !important; height: 36px !important; border-radius: 8px !important; }
        }

        /* Completed Reports list row */
        .irep-report-row {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 4px;
        }
        .irep-report-body {
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .irep-report-info { flex: 1; min-width: 0; }
        .irep-report-meta { flex-shrink: 0; text-align: right; }
        .irep-report-btns { display: flex; gap: 8px; flex-shrink: 0; }

        @media (max-width: 640px) {
          /* Header: search + export stack */
          .irep-header-row { flex-direction: column !important; align-items: stretch !important; }
          .irep-header-row > div:last-child { width: 100%; }
          .irep-header-row > div:last-child > div:first-child { flex: 1; }
          .irep-header-row > div:last-child .btn { flex-shrink: 0; }
          .irep-search-input { width: 100% !important; }

          /* Report row: info+meta on one line, buttons wrap to second line */
          .irep-report-row { flex-wrap: wrap; gap: 8px; padding: 12px 2px; }
          .irep-report-body { flex-wrap: wrap; width: 100%; gap: 8px; }
          .irep-report-info { flex: 1; min-width: 120px; }
          .irep-report-meta { flex-shrink: 0; }
          .irep-report-btns { width: 100%; justify-content: flex-end; gap: 6px; }
          .irep-report-btns .btn { font-size: 12px; padding: 5px 10px; gap: 4px; }
        }

        @media (max-width: 480px) {
          .irep-report-row { padding: 10px 0; }
          .irep-report-meta { min-width: 80px; }
        }
      `}</style>
    </div>
  )
}
